import {
  forecastCounterDamage,
  getTile,
  MAX_HP,
  unitTotalHp,
  type Coord,
  type GameState,
  type Unit,
} from "@dailywars/core/engine";

import type { PendingAttack, SelectionState } from "#/game/selection/types";

function pendingCannonAttack({
  selection,
  state,
  attacker,
}: {
  selection: Extract<SelectionState, { mode: "confirm-attack" }>;
  state: GameState;
  attacker: Unit;
}): PendingAttack | null {
  const tile = getTile(state, selection.targetPos);
  if (tile.kind !== "cannon") return null;
  const targetHp = tile.hp ?? MAX_HP;
  const owner = tile.owner;
  if (owner !== "p1" && owner !== "p2") return null;
  return {
    attackerId: selection.unitId,
    targetPos: selection.targetPos,
    forecast: selection.forecast,
    attackerKind: attacker.kind,
    attackerOwner: attacker.owner,
    targetKind: "cannon",
    targetOwner: owner,
    willKillTarget: selection.forecast >= targetHp && targetHp > 0,
    counterForecast: null,
    willKillAttacker: false,
  };
}

export function pendingAttackFromSelection({
  selection,
  state,
}: {
  selection: SelectionState;
  state: GameState;
}): PendingAttack | null {
  if (selection.mode !== "confirm-attack") return null;
  const attacker = state.units.find((u) => u.id === selection.unitId);
  if (!attacker) return null;

  if (selection.targetKind === "cannon") {
    return pendingCannonAttack({ selection, state, attacker });
  }

  const target = state.units.find((u) => u.id === selection.targetId);
  if (!target) return null;
  const attackerDest =
    selection.path.length > 0 ? (selection.path[selection.path.length - 1] as Coord) : attacker.pos;
  const targetHp = unitTotalHp(target);
  const attackerHp = unitTotalHp(attacker);
  const counterForecast = forecastCounterDamage({
    state,
    attackerId: attacker.id,
    targetId: target.id,
    attackerDest,
    attackDamage: selection.forecast,
  });

  return {
    attackerId: selection.unitId,
    targetId: selection.targetId,
    targetPos: target.pos,
    forecast: selection.forecast,
    attackerKind: attacker.kind,
    attackerOwner: attacker.owner,
    targetKind: selection.targetKind,
    targetOwner: target.owner,
    willKillTarget: selection.forecast >= targetHp && targetHp > 0,
    counterForecast,
    willKillAttacker: counterForecast !== null && counterForecast >= attackerHp && attackerHp > 0,
  };
}
